/**
 * **信封成败判定的唯一一份实现**（seq=2216 项负责定死：判成败只看 `resultCode`，`code` 不再参与判断）。
 *
 * 与 `core/utils/readState.ts` 同一条理由：判据掺进 `axios`／`element-plus` 才能跑的代码，node 就量不到它，
 * 验收只能靠人肉截图。所以本文件**除 readState 外零 import**，`client.ts` 转引这里，
 * 全仓只此一处判成败。取证回调以参数注入（`onDrift`），⛔ 在这里 import `client.ts` —— 会成环。
 *
 * ── 为什么 `code` 必须退出判定 ──────────────────────────────────────────────────────
 * `code` 是**被派生**的那一枚（PRD `:55`／`D-11 :332`／`RK16 :494` 的箭头都是 `resultCode → code` 单向），
 * 被派生的一方反过来当真源，前端就自己长出第二张映射表。更要紧的是可机械预测的那条后果：后端要把
 * `code` 翻成整数（＝HTTP 状态），翻的那一趟多枚业务码会并成同一个值（404 一档里既有「端点未部署」
 * 又有「单不存在」）。判定继续读它，落地当天就是**拿一个并档值判成败**。所以顺序是不靠同轮的
 * （项负责 seq=2215）：四端先把判定收口到 `resultCode`，真没人读 `code` 了后端才翻整数。
 *
 * ── 三条读法，别再互相顶 ────────────────────────────────────────────────────────────
 * 1. **成败**只看 `resultCode` 这枚键的**值**：码名 `OK` 才算通过（`envelopePassed`）。
 * 2. `code` 留作**取证事实位**——原样塞进 `ApiError.code` 供人看，⛔ 参与任何一枚判定分支。
 * 3. **缺载体就是不可判**，而不可判⛔ 算通过：09-18 那趟后端只新增 `resultCode`，所以打当天之前的
 *    真服务，本函数一律判不通过并记一条漂移证据。这不是误伤——把没带载体的 200 当成功，等于把
 *    「200 业务失败」（§6.0-④ 冻结那一档）静默放行成成功。
 *
 * ⛔ 把本文件的任何一枚出口拿去解析**批量条目**那层的 `items[].code`：条目级契约仍是字符串真码名
 * （`BatchItemResult.code`），与信封那枚同名不同型，两层共用一枚解析就会互相顶包（seq=2216 点名那条）。
 */

import { readErrorShapeOf } from '../utils/readState.ts';

/** 信封面上判定用得着的两枚键；`code` 只进取证回调，⛔ 进判据。 */
export interface EnvelopeShape {
  resultCode?: unknown;
  code?: unknown;
}

/** 成功码名（§6.0-③ 冻结的明文字符串）。 */
export const ENVELOPE_OK_CODE = 'OK';

/**
 * 全仓唯一一处「从信封里取业务码载体」。取值尺**不自己写**——转引 `readState.ts` 那把（`codeSource`
 * 只登记事实的语义、空串＝缺载体、数字 0 是合法码按字符串承载，三格都在它那侧定过账），⛔ 在此另立。
 */
export function envelopeCarrier(src: EnvelopeShape | null | undefined): string | undefined {
  return readErrorShapeOf({ resultCode: src?.resultCode }).businessCode;
}

/** 码名分支的唯一入口：`ApiError` 那组 getter 与登录页的 switch 都得经它，⛔ 再比 `err.code`。 */
export function envelopeCodeMatches(src: EnvelopeShape | null | undefined, codeName: string): boolean {
  return envelopeCarrier(src) === codeName;
}

/**
 * 这枚信封成不成功。`onDrift` 由调用侧注入（`client.ts` 的 `recordDrift`），两格都是**在册取证**：
 * 缺载体与大小写漂移，⛔ 静默容忍也⛔ 拿来当判定依据。
 */
export function envelopePassed(src: EnvelopeShape | null | undefined, onDrift?: (kind: string, observed: unknown) => void): boolean {
  const carrier = envelopeCarrier(src);
  if (carrier === undefined) {
    onDrift?.('信封缺 resultCode ⇒ 成败无载体，一律按不通过处置（seq=2216；`code` 已退出判定，⛔ 回读它兜底）', {
      hasCode: src !== null && src !== undefined && 'code' in src,
      code: src?.code,
    });
    return false;
  }
  if (carrier === ENVELOPE_OK_CODE) return true;
  if (carrier.toLowerCase() === 'ok') {
    onDrift?.('resultCode 非冻结大小写（成功码名固定 "OK"）', carrier);
    return true;
  }
  if (typeof src?.resultCode === 'number') {
    onDrift?.('resultCode 为 int（`:55` 要求字符串码名；翻整数那一趟动的是 `code`，⛔ 把这枚也翻）', src.resultCode);
  }
  return false;
}
